/**
 * @file apps/web/src/lib/auth.tsx
 * @description Auth context for the SI firm workspace.
 * Stores a signed JWT in localStorage as 'veridox_token'.
 * All API calls send the token as Authorization: Bearer <token>.
 * On login, userType='client' redirects to /portal/dashboard.
 *
 * Token refresh: real (non-dev) sessions are established via
 * supabase.auth.signInWithPassword() in LoginPage.tsx, and Supabase access
 * tokens expire after 1 hour. AuthProvider subscribes to
 * supabase.auth.onAuthStateChange() below and re-persists the refreshed
 * access_token whenever the Supabase client rotates it in the background
 * (autoRefreshToken, on by default) — without this, users would be signed
 * out an hour after logging in. Dev quick-login (DEV_MODE only) never
 * creates a Supabase session at all, so this subscription only ever fires
 * with a non-null session for real logins and is a no-op for dev users.
 *
 * Seed user IDs (from packages/db/src/seed.ts):
 *   Sarah Chen  (owner/lead):      22222222-0000-4000-a000-000000000001
 *   Mike Rodriguez (admin):         22222222-0000-4000-a000-000000000002
 *   Priya Patel (client_admin):     22222222-0000-4000-a000-000000000003
 *   James Walsh (viewer):           22222222-0000-4000-a000-000000000004
 *   James Thornton (client_admin):  22222222-0000-4000-a000-000000000005
 *   Tenant (Clarity Consulting):    11111111-0000-4000-a000-000000000001
 *   Project (Acme P2P):             44444444-0000-4000-a000-000000000001
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from './supabase.js';

export interface AuthUser {
  userId:    string;
  tenantId:  string;
  email:     string;
  fullName:  string;
  projectId: string;
  token:     string;
  userType:  'si' | 'client';
  // Project-scoped role (e.g. 'lead', 'consultant') — some existing checks
  // (e.g. AppShell's canManageSettings) intentionally read this as a
  // project role, not a tenant role.
  role?:     string;
  // Tenant-level role (e.g. 'owner', 'admin') — distinct from `role` above.
  // Use this for tenant-scoped checks like "SI Admin only" (Settings page's
  // Approval Settings section) — 'owner'/'admin' are never valid values of
  // the project-scoped `role` field.
  tenantRole?: string;
}

// Empty sentinel — never exposed to pages (AppShell guards before render)
const EMPTY_USER: AuthUser = {
  userId: '', tenantId: '', email: '', fullName: '', projectId: '',
  token: '', userType: 'si',
};

const STORAGE_KEY = 'veridox_token';

interface AuthContextValue {
  user:            AuthUser;
  isAuthenticated: boolean;
  setUser:         (u: AuthUser | null) => void;
  devHeaders:      Record<string, string>;
}

export const AuthContext = createContext<AuthContextValue>({
  user:            EMPTY_USER,
  isAuthenticated: false,
  setUser:         () => undefined,
  devHeaders:      {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<AuthUser | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
      return null;
    }
  });

  const setUser = (u: AuthUser | null) => {
    if (u) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    setStored(u);
  };

  // Keep the stored access token in sync with Supabase's own refresh cycle.
  // Guarded on `session` being non-null so this never fires for dev
  // quick-login users (who have no Supabase session to begin with) and
  // never clobbers a stored dev session with a stale/absent one.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.access_token) return;
      setStored((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, token: session.access_token };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        return updated;
      });
    });
    return () => subscription.unsubscribe();
  }, []);

  const user = stored ?? EMPTY_USER;

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: stored !== null,
        setUser,
        devHeaders: stored?.token
          ? { 'Authorization': `Bearer ${stored.token}` }
          : {},
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
